// ============================================================================
// use-rfq-source-doubt.ts
// ----------------------------------------------------------------------------
// The QM Assistant checks Full AI's recorded doubt about the request source
// with the operator at Stage 5 only (Peter, 2026-10-22: "it should only prompt
// user about whether to use email or the RFQ document provided in Stage 5 via
// the AI Assistant"). On arrival at Stage 5 it brings the Assistant forward
// once (the cockpit's Assistant tab; the floating panel only when no cockpit
// has mounted after a short wait, as on a phone), and the card stays at the
// top of the conversation until the operator keeps the reading, switches
// source, or closes it.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    markRfqSourcePrompt,
    openRfqSourceReadings,
    readRfqSourcePromptLedger,
    rfqSourceReadingToRaise,
    rfqSourceReadingToShow,
    writeRfqSourcePromptLedger,
    type RfqSourcePromptLedger,
    type RfqSourcePromptStage,
    type RfqSourceReadingV1,
} from '../services/rfq-source-reading';

/** Bringing the Assistant forward waits for the stage's cockpit to mount. */
export const RFQ_SOURCE_RAISE_DELAY_MS = 400;
/**
 * A Stage 5 cockpit can mount well after the editor opens (the live smoke,
 * 2026-10-21, under load): its Assistant tab is tried again before the
 * floating panel is used.
 */
export const RFQ_SOURCE_DOCK_RETRY_MS = 300;
export const RFQ_SOURCE_DOCK_ATTEMPTS = 10;

/** Stage 5 is the final quote open on the QuoteMaker page; the check is shown nowhere else. */
export function rfqSourcePromptStage({
    currentPage,
    quoteEditorOpen,
}: {
    currentPage: string;
    quoteEditorOpen: boolean;
}): RfqSourcePromptStage | null {
    return currentPage === 'quotemaker' && quoteEditorOpen ? 5 : null;
}

export interface UseRfqSourceDoubtArgs {
    projectId: string | null | undefined;
    currentPage: string;
    quoteEditorOpen: boolean;
    analysis: unknown;
    projectWMS: unknown;
    formData: Record<string, unknown> | null | undefined;
    /** False for a role with no Assistant (site assessor): nothing is raised. */
    assistantAvailable: boolean;
    /** Show the stage cockpit's Assistant tab. False when no cockpit can host it yet. */
    raiseInCockpit: () => boolean;
    /** Open the floating Assistant panel. */
    openFloatingAssistant: () => void;
}

export function useRfqSourceDoubt({
    projectId,
    currentPage,
    quoteEditorOpen,
    analysis,
    projectWMS,
    formData,
    assistantAvailable,
    raiseInCockpit,
    openFloatingAssistant,
}: UseRfqSourceDoubtArgs) {
    const promptStage = rfqSourcePromptStage({ currentPage, quoteEditorOpen });
    const coveringDecision = formData?.rfqCoveringNoteDecision;
    const formReading = formData?.rfqSourceReading;
    const rfqContent = formData?.rfqContent;
    const keptReadingIds = formData?.rfqSourceKeptReadingIds;
    const readings = useMemo(
        () => openRfqSourceReadings({
            analysis,
            projectWMS,
            formData: { rfqCoveringNoteDecision: coveringDecision, rfqSourceReading: formReading, rfqContent, rfqSourceKeptReadingIds: keptReadingIds },
        }),
        [analysis, projectWMS, coveringDecision, formReading, rfqContent, keptReadingIds],
    );
    const [ledgerState, setLedgerState] = useState<{ projectId: string | null; ledger: RfqSourcePromptLedger }>(
        () => ({ projectId: projectId || null, ledger: readRfqSourcePromptLedger(projectId) }),
    );
    // Another project's ledger is never read for this one.
    const ledger = ledgerState.projectId === (projectId || null)
        ? ledgerState.ledger
        : readRfqSourcePromptLedger(projectId);
    useEffect(() => {
        setLedgerState({ projectId: projectId || null, ledger: readRfqSourcePromptLedger(projectId) });
    }, [projectId]);

    const record = useCallback((readingId: string, event: 'raised' | 'dismissed' | 'kept', atStage: RfqSourcePromptStage) => {
        const owner = projectId || null;
        setLedgerState((previous) => {
            const base = previous.projectId === owner ? previous.ledger : readRfqSourcePromptLedger(owner);
            const next = markRfqSourcePrompt(base, readingId, event, atStage);
            writeRfqSourcePromptLedger(owner, next);
            return { projectId: owner, ledger: next };
        });
    }, [projectId]);

    const toRaise = assistantAvailable ? rfqSourceReadingToRaise(readings, promptStage, ledger) : null;
    const toRaiseId = toRaise?.id || null;
    useEffect(() => {
        if (!toRaiseId || promptStage !== 5) return undefined;
        const atStage = promptStage;
        let attempts = 0;
        let timer = setTimeout(function raise() {
            attempts += 1;
            const docked = raiseInCockpit();
            if (!docked && attempts < RFQ_SOURCE_DOCK_ATTEMPTS) {
                timer = setTimeout(raise, RFQ_SOURCE_DOCK_RETRY_MS);
                return;
            }
            if (!docked) openFloatingAssistant();
            record(toRaiseId, 'raised', atStage);
        }, RFQ_SOURCE_RAISE_DELAY_MS);
        return () => clearTimeout(timer);
        // The two callbacks are per-render arrows in App; the reading and the stage decide.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toRaiseId, promptStage, record]);

    const reading = rfqSourceReadingToShow(readings, promptStage, ledger);
    const keep = useCallback((target: RfqSourceReadingV1) => {
        if (promptStage === 5) record(target.id, 'kept', promptStage);
    }, [promptStage, record]);
    const close = useCallback((target: RfqSourceReadingV1) => {
        if (promptStage === 5) record(target.id, 'dismissed', promptStage);
    }, [promptStage, record]);

    return { reading, promptStage, keep, close };
}
